import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { createJob, getCustomers, addCustomer, uploadJobFiles } from "../../lib/supabase/assignments";
import { FiCheck, FiX, FiEdit3 } from "react-icons/fi";
import DragDropUploader from "../common/DragDropUploader";

const NewAssignment = () => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();

    const [buttonLoading, setButtonLoading] = useState(false);
    const [formErrors, setFormErrors] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [isTimberJob, setIsTimberJob] = useState(false);

    const INITIAL_FORM_STATE = {
        customerName: "",
        customerNumber: "",
        customerMail: "",
        customerAddress: "",
        uniqueId: "",
        carpenterSupervisorName: "",
        carpenterSupervisorNumber: "",
        jobType: "Design & Estimation",
        quotes: {
            roof: { isRequired: false, expectedFinishDate: "", expectedDeliveryDate: "" },
            wall: { isRequired: false, expectedFinishDate: "", expectedDeliveryDate: "" },
            floor: { isRequired: false, expectedFinishDate: "", expectedDeliveryDate: "" },
            timber: { isRequired: false, expectedDeliveryDate: "" }
        },
        jobInformation: {
            roofType: "",
            roofTrussCentres: "",
            floors: [
                {
                    floorNumber: 1,
                    floorTrussSize: "",
                    flooring: "",
                    balconyIncluded: false
                }
            ],
            isSingleStorey: true,
            singleStorey: {
                loadBearingWallStudSizes: "90x45",
                nonLoadBearingWallStudSizes: "",
                nonLoadBearingWallStudCentres: "",
                wallHeight: ""
            },
            doubleStorey: {
                groundFloor: {
                    loadBearingWallStudSizes: "90x45",
                    nonLoadBearingWallStudSizes: "",
                    nonLoadBearingWallStudCentres: "",
                    wallHeight: ""
                },
                firstFloor: {
                    loadBearingWallStudSizes: "90x45",
                    nonLoadBearingWallStudSizes: "",
                    nonLoadBearingWallStudCentres: "",
                    wallHeight: ""
                }
            },
            siteMeasure: {
                required: false,
                wall: false,
                floor: false,
                truss: false
            },
            isUnit: false,
            unitCount: "",
            notes: [],
            urgentQuote: false,
            urgentDesign: false
        },
        invoiced: false,
        estimationFinishDate: "",
        pdfs: []
    };

    const [formData, setFormData] = useState(INITIAL_FORM_STATE);
    const [newNote, setNewNote] = useState("");
    const [editingNoteIndex, setEditingNoteIndex] = useState(null);
    const [editingNoteContent, setEditingNoteContent] = useState("");
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [clearPdfs, setClearPdfs] = useState(false);
    const [dropdownVisible, setDropdownVisible] = useState(false);
    const dropdownRef = useRef(null);
    const [showAddButton, setShowAddButton] = useState(false);

    useEffect(() => {
        if (company?.id) {
            fetchCustomers();
        }
    }, [company]);

    const fetchCustomers = async () => {
        try {
            const customersData = await getCustomers(company.id);
            setCustomers(customersData);
        } catch (error) {
            console.error("Error fetching customers:", error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (buttonLoading) return;

        // Set timber quote if it's a timber job
        if (isTimberJob) {
            formData.quotes.timber.isRequired = true;
            formData.quotes.roof.isRequired = false;
            formData.quotes.wall.isRequired = false;
            formData.quotes.floor.isRequired = false;
            formData.jobType = "Timber";
        }

        const validationErrors = validateForm();
        setFormErrors(validationErrors);

        if (validationErrors.length > 0) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        setButtonLoading(true);

        try {
            // 1. Create the job
            const { job } = await createJob(
                formData,
                company.id,
                userProfile.id,
                userProfile.username || userProfile.full_name
            );

            // 2. Upload files if any
            if (formData.pdfs.length > 0) {
                await uploadJobFiles(
                    job.id,
                    formData.pdfs,
                    userProfile.id,
                    userProfile.username || userProfile.full_name,
                    company.slug
                );
            }

            alert("Job created successfully!");

            // Reset form
            setFormData({ ...INITIAL_FORM_STATE });
            setIsTimberJob(false);
            setInputValue("");
            setFormErrors([]);
            setClearPdfs(true);
            setTimeout(() => setClearPdfs(false), 1000);

            // Navigate to assignments page
            navigate(`/${company.slug}/assignment/list`);
        } catch (error) {
            console.error("Error creating job:", error);
            alert("Error creating job: " + error.message);
        } finally {
            setButtonLoading(false);
        }
    };

    const validateForm = () => {
        const errors = [];

        if (!formData.uniqueId?.trim()) {
            errors.push("Job ID is required");
        }

        if (!formData.customerName?.trim()) {
            errors.push("Customer name is required");
        }

        if (isTimberJob) {
            return errors;
        }

        if (!formData.jobType) {
            errors.push("Job type is required");
        }

        const hasSelectedSection = formData.quotes.roof.isRequired ||
            formData.quotes.wall.isRequired ||
            formData.quotes.floor.isRequired;

        if (!hasSelectedSection) {
            errors.push("At least one section (Roof, Wall, or Floor) must be selected");
        }

        return errors;
    };

    // Add this component before the main return
    const ErrorDisplay = ({ errors }) => {
        if (errors.length === 0) return null;

        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <h3 className="text-red-800 font-medium mb-2">Please fix the following errors:</h3>
                <ul className="text-red-700 text-sm space-y-1">
                    {errors.map((error, index) => (
                        <li key={index} className="flex items-start">
                            <span className="text-red-500 mr-2">•</span>
                            {error}
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    const addNote = () => {
        if (!newNote.trim()) return;

        const noteObject = {
            content: newNote.trim(),
            addedBy: {
                userId: "temp-admin", // Will be set by backend
                name: "Admin",
                role: "admin"
            },
            addedAt: new Date().toISOString(),
            isEdited: false
        };

        setFormData(prevState => ({
            ...prevState,
            jobInformation: {
                ...prevState.jobInformation,
                notes: [...prevState.jobInformation.notes, noteObject]
            }
        }));

        setNewNote("");
    };

    const startEditNote = (index, content) => {
        setEditingNoteIndex(index);
        setEditingNoteContent(content);
    };

    const saveEditNote = (index) => {
        if (!editingNoteContent.trim()) return;

        const updatedNotes = [...formData.jobInformation.notes];
        updatedNotes[index] = {
            ...updatedNotes[index],
            content: editingNoteContent.trim(),
            isEdited: true,
            editedAt: new Date().toISOString()
        };

        setFormData(prevState => ({
            ...prevState,
            jobInformation: {
                ...prevState.jobInformation,
                notes: updatedNotes
            }
        }));

        cancelEditNote();
    };

    const cancelEditNote = () => {
        setEditingNoteIndex(null);
        setEditingNoteContent("");
    };

    const deleteNote = (index) => {
        if (window.confirm("Are you sure you want to delete this note?")) {
            const updatedNotes = formData.jobInformation.notes.filter((_, i) => i !== index);
            setFormData(prevState => ({
                ...prevState,
                jobInformation: {
                    ...prevState.jobInformation,
                    notes: updatedNotes
                }
            }));
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "";
        return new Date(dateString).toLocaleDateString("en-AU", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    // Custom button component for selections
    const SelectionButton = ({ label, isSelected, onClick, className = "" }) => (
        <button
            type="button"
            onClick={onClick}
            className={`px-4 py-2 rounded-lg border-2 transition-all duration-200 font-medium ${isSelected
                ? 'bg-blue-500 text-white border-blue-500 shadow-md'
                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300 hover:bg-blue-50'
                } ${className}`}
        >
            {label}
        </button>
    );

    // Custom checkbox button component
    const CheckboxButton = ({ label, isChecked, onClick, className = "" }) => (
        <button
            type="button"
            onClick={onClick}
            className={`px-4 py-2 rounded-lg border-2 transition-all duration-200 font-medium flex items-center gap-2 ${isChecked
                ? 'bg-green-500 text-white border-green-500 shadow-md'
                : 'bg-white text-gray-700 border-gray-300 hover:border-green-300 hover:bg-green-50'
                } ${className}`}
        >
            {isChecked ? <FiCheck className="w-4 h-4" /> : <FiX className="w-4 h-4" />}
            {label}
        </button>
    );

    const renderWallSpecs = (prefix, data, title) => (
        <div className="bg-red-100/70 p-4 rounded-lg space-y-4 mt-4">
            <h4 className="text-lg font-semibold text-gray-800">{title}</h4>

            <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Non Load Bearing Wall Stud Sizes</p>
                <div className="flex gap-2 sm:flex-row flex-col">
                    <SelectionButton
                        label="90x45"
                        isSelected={data.nonLoadBearingWallStudSizes === "90x45"}
                        onClick={() => handleToggleSelection(`jobInformation.${prefix}.nonLoadBearingWallStudSizes`, "90x45")}
                    />
                    <SelectionButton
                        label="90x35"
                        isSelected={data.nonLoadBearingWallStudSizes === "90x35"}
                        onClick={() => handleToggleSelection(`jobInformation.${prefix}.nonLoadBearingWallStudSizes`, "90x35")}
                    />
                </div>
            </div>

            <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Non Load Bearing Wall Stud Centres</p>
                <div className="flex gap-2 sm:flex-row flex-col">
                    <SelectionButton
                        label="450"
                        isSelected={data.nonLoadBearingWallStudCentres === "450"}
                        onClick={() => handleToggleSelection(`jobInformation.${prefix}.nonLoadBearingWallStudCentres`, "450")}
                    />
                    <SelectionButton
                        label="600"
                        isSelected={data.nonLoadBearingWallStudCentres === "600"}
                        onClick={() => handleToggleSelection(`jobInformation.${prefix}.nonLoadBearingWallStudCentres`, "600")}
                    />
                </div>
            </div>

            <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Wall Height</p>
                <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    type="text"
                    value={data.wallHeight}
                    onChange={(e) => handleNestedChange(`jobInformation.${prefix}.wallHeight`, e.target.value)}
                    placeholder="Wall Height (general without stepdown)"
                />
            </div>
        </div>
    );

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name.includes('.')) {
            handleNestedChange(name, type === 'checkbox' ? checked : value);
            return;
        }

        const filtered = customers.filter((customer) =>
            customer.name.toLowerCase().includes(value.toLowerCase())
        );

        if (name === "customerNumber") {
            const phonePattern = /^[0-9]{0,10}$/;
            if (!phonePattern.test(value)) return;

            const landlinePattern = /^(?:02|03|07|08)[0-9]{8}$/;
            const mobilePattern = /^04[0-9]{8}$/;
            const isValid = landlinePattern.test(value) || mobilePattern.test(value);

            if (!isValid && value.length === 10) {
                alert("Invalid Australian phone number");
            }

            setFormData(prevState => ({ ...prevState, customerNumber: value }));
        } else if (name === "carpenterSupervisorNumber") {
            const phonePattern = /^[0-9]{0,10}$/;
            if (!phonePattern.test(value)) return;

            const landlinePattern = /^(?:02|03|07|08)[0-9]{8}$/;
            const mobilePattern = /^04[0-9]{8}$/;
            const isValid = landlinePattern.test(value) || mobilePattern.test(value);

            if (!isValid && value.length === 10) {
                alert("Invalid Australian phone number");
            }

            setFormData(prevState => ({ ...prevState, carpenterSupervisorNumber: value }));
        } else if (name === "customerName") {
            setInputValue(value);
            setFilteredCustomers(filtered);
            setDropdownVisible(value.length > 0);
            setFormData(prevState => ({ ...prevState, customerName: value }));
        } else {
            setFormData(prevState => ({ ...prevState, [name]: type === 'checkbox' ? checked : value }));
        }

        setShowAddButton(filtered.length === 0 && value.trim() !== "" && formData.customerNumber.trim() !== "");
    };

    const handleNestedChange = (path, value) => {
        const keys = path.split('.');
        setFormData(prevState => {
            const newState = { ...prevState };
            let current = newState;

            for (let i = 0; i < keys.length - 1; i++) {
                current = current[keys[i]];
            }

            current[keys[keys.length - 1]] = value;
            return newState;
        });
    };

    // Helper function to toggle selection - if already selected, reset to empty/initial value
    const handleToggleSelection = (path, value, initialValue = "") => {
        const keys = path.split('.');
        let current = formData;

        for (let i = 0; i < keys.length - 1; i++) {
            current = current[keys[i]];
        }

        const currentValue = current[keys[keys.length - 1]];
        const newValue = currentValue === value ? initialValue : value;

        handleNestedChange(path, newValue);
    };

    const handleCustomerSelect = (customer) => {
        setFormData(prevState => ({
            ...prevState,
            customerName: customer.name,
            customerNumber: customer.phone.substring(3),
        }));

        setFilteredCustomers([]);
        setInputValue(customer.name);
        setDropdownVisible(false);
        setShowAddButton(false);
    };

    const handleInputClick = () => {
        if (inputValue.length > 0) {
            setDropdownVisible(true);
        }
    };

    const handleAddCustomer = () => {
        // const token = Cookies.get("token");
        // if (!token) {
        //     navigate("/login");
        // } else {
        //     axios
        //         .post(
        //             `${process.env[`REACT_APP_R_BASE_SERVER_URL_${company.toUpperCase()}`]}/admin/add-customer`,
        //             {
        //                 name: formData.customerName,
        //                 phone: `+61${formData.customerNumber}`,
        //             },
        //             {
        //                 headers: { Authorization: `Bearer ${token}` },
        //             }
        //         )
        //         .then((response) => {
        //             alert(response.data.message);
        //             setCustomers([...customers, response.data.customer]);
        //             setDropdownVisible(false);
        //             setShowAddButton(false);
        //         })
        //         .catch((error) => {
        //             console.error("Error adding customer:", error);
        //             alert("Error adding customer.");
        //         });
        // }
    };

    const handlePdfsChange = (pdfFiles) => {
        setFormData(prevState => ({ ...prevState, pdfs: pdfFiles }));
    };

    return (
        <>
            <form onSubmit={handleSubmit} className="space-y-8">
                <div className="text-start">
                    <h1 className="sm:text-3xl text-2xl font-bold text-gray-900 mb-2">{isTimberJob ? 'New Timber Job' : 'New Construction Job'}</h1>
                    <p className="text-gray-600">{isTimberJob ? 'Create a new timber job assignment' : 'Create a new construction job assignment'}</p>
                </div>

                {/* Job Category Toggle */}
                <div className="space-y-3">
                    <div className="flex items-start justify-start">
                        <button
                            type="button"
                            onClick={() => setIsTimberJob(!isTimberJob)}
                            className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${isTimberJob
                                ? 'bg-amber-500 text-white hover:bg-amber-600'
                                : 'bg-blue-500 text-white hover:bg-blue-600'
                                }`}
                        >
                            {isTimberJob ? 'Timber Job' : 'Construction Job'}
                        </button>
                    </div>
                    <p className="text-start text-sm text-gray-500">
                        {isTimberJob ? 'Click to switch to Construction Job' : 'Click to switch to Timber Job'}
                    </p>
                </div>

                {/* Job ID */}
                <div className="space-y-4">
                    <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-purple-500 pb-2">Job Identification</h2>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Job ID / Unique Identifier</label>
                        <input
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            type="text"
                            name="uniqueId"
                            value={formData.uniqueId}
                            onChange={handleChange}
                            placeholder="Enter unique job identifier"
                            required
                        />
                    </div>
                </div>

                {/* Customer Information */}
                <div className="space-y-4">
                    <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-blue-500 pb-2">Customer Information</h2>

                    <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Customer Name</label>
                        <input
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            type="text"
                            value={inputValue}
                            onChange={handleChange}
                            onClick={handleInputClick}
                            name="customerName"
                            placeholder="Enter customer name"
                            required
                        />
                        {dropdownVisible && filteredCustomers.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg" ref={dropdownRef}>
                                {filteredCustomers.slice(0, 5).map((customer) => (
                                    <button
                                        key={customer._id}
                                        type="button"
                                        className="w-full px-4 py-2 text-left hover:bg-gray-100 first:rounded-t-lg last:rounded-b-lg"
                                        onClick={() => handleCustomerSelect(customer)}
                                    >
                                        {customer.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Customer Phone Number</label>
                        <div className="flex">
                            <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                                +61
                            </span>
                            <input
                                className="flex-1 px-4 py-3 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
                                type="text"
                                name="customerNumber"
                                value={formData.customerNumber}
                                onChange={handleChange}
                                placeholder="Customer phone number"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Customer Email Address</label>

                        <input
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            type="email"
                            name="customerMail"
                            value={formData.customerMail}
                            onChange={handleChange}
                            placeholder="Customer email address"
                        />

                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Customer & Delivery Address</label>

                        <textarea
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            type="text"
                            name="customerAddress"
                            value={formData.customerAddress}
                            onChange={handleChange}
                            placeholder="Customer & delivery address"
                        />

                    </div>

                    {showAddButton && (
                        <button
                            type="button"
                            onClick={handleAddCustomer}
                            className="w-full py-2 px-4 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                        >
                            Add to Customers
                        </button>
                    )}
                </div>

                {/* Timber Job Expected Delivery Date */}
                {isTimberJob && (
                    <div className="space-y-4">
                        <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-orange-500 pb-2">Delivery Information</h2>
                        <div>
                            <p className="text-sm font-medium text-gray-700 mb-2">Expected Delivery Date</p>
                            <input
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                type="date"
                                value={formData?.quotes?.timber?.expectedDeliveryDate || ""}
                                onChange={(e) => handleNestedChange("quotes.timber.expectedDeliveryDate", e.target.value)}
                            />
                        </div>
                    </div>
                )}

                {/* Supervisor Information */}
                {!isTimberJob && (
                    <div className="space-y-4">
                        <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-blue-500 pb-2">Carpenter <span className="text-sm text-black/40 mx-1">or</span> Supervisor</h2>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                            <input
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                type="text"
                                name="carpenterSupervisorName"
                                value={formData.carpenterSupervisorName}
                                onChange={handleChange}
                                placeholder="Enter supervisor name"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                            <div className="flex">
                                <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                                    +61
                                </span>
                                <input
                                    className="flex-1 px-4 py-3 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
                                    type="text"
                                    name="carpenterSupervisorNumber"
                                    value={formData.carpenterSupervisorNumber}
                                    onChange={handleChange}
                                    placeholder="Supervisor phone number"
                                />
                            </div>
                        </div>
                    </div>
                )}


                {/* Job Type */}
                {!isTimberJob && (
                    <div className="space-y-4">
                        <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-blue-500 pb-2">Job Type</h2>
                        <div className="sm:flex-row flex gap-3 flex-col">
                            <SelectionButton
                                label="Design Only"
                                isSelected={formData.jobType === "Design"}
                                onClick={() => setFormData(prev => ({ ...prev, jobType: "Design" }))}
                            />
                            <SelectionButton
                                label="Estimation Only"
                                isSelected={formData.jobType === "Estimation"}
                                onClick={() => setFormData(prev => ({ ...prev, jobType: "Estimation" }))}
                            />
                            <SelectionButton
                                label="Both Design & Estimation"
                                isSelected={formData.jobType === "Design & Estimation"}
                                onClick={() => setFormData(prev => ({ ...prev, jobType: "Design & Estimation" }))}
                            />
                        </div>
                    </div>
                )}

                {!isTimberJob && (
                    <>
                        {/* Quote Selection */}
                        < div className="space-y-4">
                            <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-blue-500 pb-2">Required Sections</h2>
                            <p className="text-sm text-gray-600">Select which sections are needed for this job</p>


                            {!formData.quotes.roof.isRequired && !formData.quotes.wall.isRequired && !formData.quotes.floor.isRequired && (
                                <p className="text-red-500 text-sm">⚠️ Please select at least one section</p>
                            )}


                            <div className="flex gap-3 flex-wrap flex-col sm:flex-row">
                                <CheckboxButton
                                    label="Roof"
                                    isChecked={formData.quotes.roof.isRequired}
                                    onClick={() => handleNestedChange('quotes.roof.isRequired', !formData.quotes.roof.isRequired)}
                                />
                                <CheckboxButton
                                    label="Wall"
                                    isChecked={formData.quotes.wall.isRequired}
                                    onClick={() => handleNestedChange('quotes.wall.isRequired', !formData.quotes.wall.isRequired)}
                                />
                                <CheckboxButton
                                    label="Floor"
                                    isChecked={formData.quotes.floor.isRequired}
                                    onClick={() => handleNestedChange('quotes.floor.isRequired', !formData.quotes.floor.isRequired)}
                                />
                            </div>
                        </div>
                    </>
                )}

                {/* Job Specifications */}
                {!isTimberJob && (
                    <>
                        {(formData.quotes.roof.isRequired || formData.quotes.wall.isRequired || formData.quotes.floor.isRequired) && (
                            <div className="space-y-6">
                                <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-blue-500 pb-2">Job Specifications</h2>

                                {/* Roof Specifications */}
                                {formData.quotes.roof.isRequired && (
                                    <div className="bg-blue-50 p-4 rounded-lg space-y-4">
                                        <h3 className="text-lg font-semibold text-gray-800">Roof Specifications</h3>

                                        <div>
                                            <p className="text-sm font-medium text-gray-700 mb-2">Roof Type</p>
                                            <div className="flex gap-2 sm:flex-row flex-col">
                                                <SelectionButton
                                                    label="Tiled"
                                                    isSelected={formData.jobInformation.roofType === "Tiled"}
                                                    onClick={() => {
                                                        if (formData.jobInformation.roofType === "Tiled") {
                                                            handleNestedChange('jobInformation.roofType', "");
                                                            handleNestedChange('jobInformation.roofTrussCentres', "");
                                                        } else {
                                                            handleNestedChange('jobInformation.roofType', "Tiled");
                                                            handleNestedChange('jobInformation.roofTrussCentres', "600");
                                                        }
                                                    }}
                                                />
                                                <SelectionButton
                                                    label="Colorbond"
                                                    isSelected={formData.jobInformation.roofType === "Colorbond"}
                                                    onClick={() => handleToggleSelection('jobInformation.roofType', "Colorbond")}
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <p className="text-sm font-medium text-gray-700 mb-2">Roof Truss Centres</p>
                                            <div className="flex gap-2 flex-wrap">
                                                <SelectionButton
                                                    label="600"
                                                    isSelected={formData.jobInformation.roofTrussCentres === "600"}
                                                    onClick={() => handleToggleSelection('jobInformation.roofTrussCentres', "600")}
                                                />
                                                {formData?.jobInformation?.roofType !== "Tiled" &&
                                                    <SelectionButton
                                                        label="900"
                                                        isSelected={formData.jobInformation.roofTrussCentres === "900"}
                                                        onClick={() => handleToggleSelection('jobInformation.roofTrussCentres', "900")}
                                                    />
                                                }
                                            </div>
                                        </div>

                                        {/* Estimation Date Roof */}
                                        <div>
                                            <p className="text-sm font-medium text-gray-700 mb-2">Expected Finish Date</p>
                                            <div className="flex gap-2 flex-wrap">
                                                <input
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    type="date"
                                                    name="estimationFinishDate"
                                                    value={formData?.quotes?.roof?.expectedFinishDate || ""}
                                                    onChange={(e) => handleNestedChange("quotes.roof.expectedFinishDate", e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        {/* Expected Delivery Date Roof */}
                                        <div>
                                            <p className="text-sm font-medium text-gray-700 mb-2">Expected Delivery Date</p>
                                            <div className="flex gap-2 flex-wrap">
                                                <input
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    type="date"
                                                    value={formData?.quotes?.roof?.expectedDeliveryDate || ""}
                                                    onChange={(e) => handleNestedChange("quotes.roof.expectedDeliveryDate", e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}


                                {/* Wall Specifications */}
                                {(formData.quotes.wall.isRequired) && (
                                    <div className="bg-red-50 p-4 rounded-lg space-y-4">
                                        <h3 className="text-lg font-semibold text-gray-800">Wall Specifications</h3>

                                        <div>
                                            <p className="text-sm font-medium text-gray-700 mb-2">Building Type</p>
                                            <div className="flex gap-2 sm:flex-row flex-col">
                                                <SelectionButton
                                                    label="Single Storey"
                                                    isSelected={formData.jobInformation.isSingleStorey === true}
                                                    onClick={() => handleToggleSelection('jobInformation.isSingleStorey', true, null)}
                                                />
                                                <SelectionButton
                                                    label="Double Storey"
                                                    isSelected={formData.jobInformation.isSingleStorey === false}
                                                    onClick={() => handleToggleSelection('jobInformation.isSingleStorey', false, null)}
                                                />
                                            </div>
                                            {formData.jobInformation.isSingleStorey ? (
                                                renderWallSpecs('singleStorey', formData.jobInformation.singleStorey, 'Single Storey Specifications')
                                            ) : (
                                                <div className="space-y-4 mt-2">
                                                    {renderWallSpecs('doubleStorey.groundFloor', formData.jobInformation.doubleStorey.groundFloor, 'Ground Floor Specifications')}
                                                    {renderWallSpecs('doubleStorey.firstFloor', formData.jobInformation.doubleStorey.firstFloor, 'First Floor Specifications')}
                                                </div>
                                            )}
                                        </div>

                                        {/* Estimation Date Wall */}
                                        <div>
                                            <p className="text-sm font-medium text-gray-700 mb-2">Expected Finish Date</p>
                                            <div className="flex gap-2 flex-wrap">
                                                <input
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    type="date"
                                                    name="estimationFinishDate"
                                                    value={formData?.quotes?.wall?.expectedFinishDate || ""}
                                                    onChange={(e) => handleNestedChange("quotes.wall.expectedFinishDate", e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        {/* Expected Delivery Date Wall */}
                                        <div>
                                            <p className="text-sm font-medium text-gray-700 mb-2">Expected Delivery Date</p>
                                            <div className="flex gap-2 flex-wrap">
                                                <input
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    type="date"
                                                    value={formData?.quotes?.wall?.expectedDeliveryDate || ""}
                                                    onChange={(e) => handleNestedChange("quotes.wall.expectedDeliveryDate", e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Floor Specifications */}
                                {(formData.quotes.floor.isRequired) && (
                                    <div className="bg-green-50 p-4 rounded-lg space-y-4">
                                        <h3 className="text-lg font-semibold text-gray-800">Floor Specifications</h3>

                                        {/* Floor Count */}
                                        <div>
                                            <p className="text-sm font-medium text-gray-700 mb-2">Number of Floors</p>
                                            <div className="flex gap-2 items-center">
                                                <input
                                                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    type="number"
                                                    min="1"
                                                    max="10"
                                                    value={formData.jobInformation.floors.length}
                                                    onChange={(e) => {
                                                        const floorCount = parseInt(e.target.value) || 1;
                                                        const currentFloors = formData.jobInformation.floors;

                                                        if (floorCount > currentFloors.length) {
                                                            // Add new floors
                                                            const newFloors = [...currentFloors];
                                                            for (let i = currentFloors.length; i < floorCount; i++) {
                                                                newFloors.push({
                                                                    floorNumber: i + 1,
                                                                    floorTrussSize: "",
                                                                    flooring: "",
                                                                    balconyIncluded: false
                                                                });
                                                            }
                                                            handleNestedChange('jobInformation.floors', newFloors);
                                                        } else if (floorCount < currentFloors.length) {
                                                            // Remove floors
                                                            const newFloors = currentFloors.slice(0, floorCount);
                                                            handleNestedChange('jobInformation.floors', newFloors);
                                                        }
                                                    }}
                                                    placeholder="Floors"
                                                />
                                                <span className="text-sm text-gray-500">floors</span>
                                            </div>
                                        </div>

                                        {/* Individual Floor Settings */}
                                        {formData.jobInformation.floors.map((floor, index) => (
                                            <div key={index} className="bg-white p-4 rounded-lg border border-green-200 space-y-4">
                                                <h4 className="text-md font-semibold text-gray-800">
                                                    Floor {floor.floorNumber}
                                                </h4>

                                                <div>
                                                    <p className="text-sm font-medium text-gray-700 mb-2">Floor Truss Size</p>
                                                    <div className="flex gap-2 sm:flex-row flex-col flex-wrap">
                                                        {["310", "370", "420", "Longreach"].map(size => (
                                                            <SelectionButton
                                                                key={size}
                                                                label={size}
                                                                isSelected={floor.floorTrussSize === size}
                                                                onClick={() => {
                                                                    const newFloors = [...formData.jobInformation.floors];
                                                                    newFloors[index].floorTrussSize = floor.floorTrussSize === size ? "" : size;
                                                                    handleNestedChange('jobInformation.floors', newFloors);
                                                                }}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>

                                                <div>
                                                    <p className="text-sm font-medium text-gray-700 mb-2">Flooring Type</p>
                                                    <div className="flex gap-2 sm:flex-row flex-col">
                                                        <SelectionButton
                                                            label="Yellow tongue"
                                                            isSelected={floor.flooring === "Yellow tongue"}
                                                            onClick={() => {
                                                                const newFloors = [...formData.jobInformation.floors];
                                                                newFloors[index].flooring = floor.flooring === "Yellow tongue" ? "" : "Yellow tongue";
                                                                handleNestedChange('jobInformation.floors', newFloors);
                                                            }}
                                                        />
                                                        <SelectionButton
                                                            label="Compression sheet"
                                                            isSelected={floor.flooring === "Compression sheet"}
                                                            onClick={() => {
                                                                const newFloors = [...formData.jobInformation.floors];
                                                                newFloors[index].flooring = floor.flooring === "Compression sheet" ? "" : "Compression sheet";
                                                                handleNestedChange('jobInformation.floors', newFloors);
                                                            }}
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <CheckboxButton
                                                        label="Balcony Included"
                                                        isChecked={floor.balconyIncluded}
                                                        onClick={() => {
                                                            const newFloors = [...formData.jobInformation.floors];
                                                            newFloors[index].balconyIncluded = !newFloors[index].balconyIncluded;
                                                            handleNestedChange('jobInformation.floors', newFloors);
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                        {/* Estimation Date Floor */}
                                        <div>
                                            <p className="text-sm font-medium text-gray-700 mb-2">Expected Finish Date</p>
                                            <div className="flex gap-2 flex-wrap">
                                                <input
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    type="date"
                                                    name="estimationFinishDate"
                                                    value={formData?.quotes?.floor?.expectedFinishDate || ""}
                                                    onChange={(e) => handleNestedChange("quotes.floor.expectedFinishDate", e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        {/* Expected Delivery Date Floor */}
                                        <div>
                                            <p className="text-sm font-medium text-gray-700 mb-2">Expected Delivery Date</p>
                                            <div className="flex gap-2 flex-wrap">
                                                <input
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    type="date"
                                                    value={formData?.quotes?.floor?.expectedDeliveryDate || ""}
                                                    onChange={(e) => handleNestedChange("quotes.floor.expectedDeliveryDate", e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}


                                {/* Site Measure */}
                                <div className="bg-yellow-50 p-4 rounded-lg space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-800">Site Measure</h3>

                                    <CheckboxButton
                                        label="Site Measure Required"
                                        isChecked={formData.jobInformation.siteMeasure.required}
                                        onClick={() => handleNestedChange('jobInformation.siteMeasure.required', !formData.jobInformation.siteMeasure.required)}
                                    />

                                    {formData.jobInformation.siteMeasure.required && (
                                        <div className="flex gap-2 sm:flex-row flex-col">
                                            {formData.quotes.wall.isRequired && (
                                                <CheckboxButton
                                                    label="Wall"
                                                    isChecked={formData.jobInformation.siteMeasure.wall}
                                                    onClick={() => handleNestedChange('jobInformation.siteMeasure.wall', !formData.jobInformation.siteMeasure.wall)}
                                                />
                                            )}
                                            {formData.quotes.floor.isRequired && (
                                                <CheckboxButton
                                                    label="Floor"
                                                    isChecked={formData.jobInformation.siteMeasure.floor}
                                                    onClick={() => handleNestedChange('jobInformation.siteMeasure.floor', !formData.jobInformation.siteMeasure.floor)}
                                                />
                                            )}
                                            {formData.quotes.roof.isRequired && (
                                                <CheckboxButton
                                                    label="Truss"
                                                    isChecked={formData.jobInformation.siteMeasure.truss}
                                                    onClick={() => handleNestedChange('jobInformation.siteMeasure.truss', !formData.jobInformation.siteMeasure.truss)}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Unit Information */}
                {!isTimberJob && (
                    <div className="space-y-4">
                        <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-blue-500 pb-2">Additional Information</h2>

                        <div className="space-y-3">
                            <CheckboxButton
                                label="Is this a unit?"
                                isChecked={formData?.jobInformation?.isUnit}
                                onClick={() => handleNestedChange('jobInformation.isUnit', !formData?.jobInformation?.isUnit)}
                            />

                            {formData?.jobInformation?.isUnit && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Number of Units</label>
                                    <input
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        type="number"
                                        value={formData.jobInformation.unitCount}
                                        onChange={(e) => handleNestedChange('jobInformation.unitCount', e.target.value)}
                                        placeholder="How many units?"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Priority Flags */}
                {!isTimberJob && (
                    <div className="space-y-4">
                        <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-orange-500 pb-2">Priority Settings</h2>
                        <div className="flex gap-3 sm:flex-row flex-col">
                            {formData?.jobType !== "Design" &&
                                <CheckboxButton
                                    label="Urgent Quote"
                                    isChecked={formData.jobInformation.urgentQuote}
                                    onClick={() => handleNestedChange('jobInformation.urgentQuote', !formData.jobInformation.urgentQuote)}
                                />
                            }
                            {formData?.jobType !== "Estimation" &&
                                <CheckboxButton
                                    label="Urgent Design"
                                    isChecked={formData.jobInformation.urgentDesign}
                                    onClick={() => handleNestedChange('jobInformation.urgentDesign', !formData.jobInformation.urgentDesign)}
                                />
                            }
                        </div>
                    </div>
                )}

                {/* Important Dates */}
                <div className="space-y-4">
                    <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-blue-500 pb-2">Important Dates</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {!isTimberJob && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Estimation Finish Date</label>
                                <input
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    type="date"
                                    name="estimationFinishDate"
                                    value={formData.estimationFinishDate}
                                    onChange={handleChange}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Notes */}
                <div className="space-y-4">
                    <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-blue-500 pb-2">Notes</h2>

                    {/* Add New Note */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Add Note</label>
                        <textarea
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            placeholder="Add any additional notes or requirements..."
                            rows="3"
                        />
                        {newNote.trim() && (
                            <button
                                type="button"
                                onClick={addNote}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
                            >
                                Add Note
                            </button>
                        )}
                    </div>

                    {/* Existing Notes */}
                    {formData.jobInformation.notes.length > 0 && (
                        <div className="space-y-3">
                            <p className="text-sm font-medium text-gray-700">Previous Notes ({formData.jobInformation.notes.length}):</p>
                            {formData.jobInformation.notes.map((note, index) => (
                                <div key={index} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                                    {editingNoteIndex === index ? (
                                        // Edit Mode
                                        <div className="space-y-2">
                                            <textarea
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
                                                value={editingNoteContent}
                                                onChange={(e) => setEditingNoteContent(e.target.value)}
                                                rows="3"
                                            />
                                            <div className="flex gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => saveEditNote(index)}
                                                    className="px-3 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600 transition-colors"
                                                >
                                                    Save
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={cancelEditNote}
                                                    className="px-3 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600 transition-colors"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        // View Mode
                                        <>
                                            <p className="text-sm text-gray-700 whitespace-pre-wrap mb-2">{note.content}</p>
                                            <div className="flex items-center justify-between">
                                                <div className="text-xs text-gray-500">
                                                    <span>By Admin • {formatDate(note.addedAt || new Date())}</span>
                                                    {note.isEdited && <span className="ml-2">• Edited</span>}
                                                </div>
                                                <div className="flex gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => startEditNote(index, note.content)}
                                                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                                        title="Edit note"
                                                    >
                                                        <FiEdit3 className="w-3 h-3" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => deleteNote(index)}
                                                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                                        title="Delete note"
                                                    >
                                                        <FiX className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Financial */}
                <div className="space-y-4">
                    <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-green-500 pb-2">Financial</h2>
                    <CheckboxButton
                        label="Invoiced"
                        isChecked={formData.invoiced}
                        onClick={() => setFormData(prev => ({ ...prev, invoiced: !prev.invoiced }))}
                    />
                </div>


                {/* File Upload */}
                <div className="space-y-4">
                    <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-purple-500 pb-2">Plans & Documents</h2>
                    <DragDropUploader
                        onFilesChange={handlePdfsChange}
                        clearFiles={clearPdfs}
                        acceptedTypes=".pdf,.doc,.docx,.jpg,.jpeg,.png,.dwg"
                    />
                </div>


                {/* Submit Button */}
                <div className="pt-6 border-t border-gray-200">
                    <button
                        type="submit"
                        disabled={buttonLoading}
                        className="w-full py-4 px-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-blue-700 focus:ring-4 focus:ring-blue-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {buttonLoading ? (
                            <div className="flex items-center justify-center gap-2">
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Creating Job...
                            </div>
                        ) : (
                            "Create Construction Job"
                        )}
                    </button>
                </div>

                <ErrorDisplay errors={formErrors} />

            </form>
        </>
    );
};

export default NewAssignment;